'use client';

import React, { createRef, ReactNode, useRef, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import chevronArrowIcon from '@/assets/icons/chevron-down-v2-icon.svg';
import { useDetectClickOutside } from '@/helpers/hooks';
import useMenuPlacement from '@/helpers/hooks/useMenuPlacement';
import cn from '@/helpers/utils/classNames';

import MenuBar, { MenuLinkProps, useMenuBarContext } from '../Header/MenuBar';
import { DropDownContext } from './context';
import DropdownColumn from './DropdownColumn';
import DropdownTwoColumn from './DropdownTwoColumn';

import '@/styles/globals.v2.css';

export interface DropdownProps {
  label: string;
  href?: string;
  className?: string;
  labelClassName?: string;
  baseWrapperClassName?: string;
  children: ReactNode;
  arrowClassName?: string;
  isUserName?: boolean;
}

function BaseWrapperComponent({
  children,
  className,
  ...linkProps
}: { children: ReactNode; className?: string } & MenuLinkProps) {
  try {
    useMenuBarContext();
    return <MenuBar.Link {...linkProps}>{children}</MenuBar.Link>;
  } catch (error) {
    return (
      <div className={cn('v2-px-5 v2-uppercase v2-leading-4 v2-tracking-wider', className)}>
        <Link {...linkProps} className="v2-flex v2-items-center">
          {children}
        </Link>
      </div>
    );
  }
}

export default function Dropdown(props: DropdownProps) {
  const {
    children,
    label,
    className,
    baseWrapperClassName,
    labelClassName,
    arrowClassName,
    href = '#',
    isUserName,
  } = props;
  const [isActive, setIsActive] = useState(false);

  const dropdownRef = createRef<HTMLDivElement>();
  const timeout = useRef<NodeJS.Timeout | null>(null);

  useDetectClickOutside(() => setIsActive(false), dropdownRef);
  useMenuPlacement(dropdownRef, 1);

  const handleMouseOver = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }

    setIsActive(true);
  };

  const handleMouseOut = () => {
    timeout.current = setTimeout(() => {
      setIsActive(false);
    }, 100);
  };

  return (
    <DropDownContext.Provider value={{ isActive, setInActive: () => setIsActive(false) }}>
      <div
        ref={dropdownRef}
        className={cn('v2-relative', className)}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        onClick={e => e.preventDefault()}
      >
        <BaseWrapperComponent className={cn(baseWrapperClassName)} href={href}>
          <span
            className={cn(
              'v2-font-sweet-sans-pro v2-font-normal v2-text-gray-900 v2-text-base v2-leading-[18px] capitalize',
              labelClassName
            )}
          >
            {isUserName ? <span id="username-dropdown">{label}</span> : <>{label}</>}
          </span>

          <span>
            <Image src={chevronArrowIcon} alt="open dropdown" width={9} className={cn(arrowClassName, 'ml-[7px]')} />
          </span>
        </BaseWrapperComponent>

        {isActive ? children : null}
      </div>
    </DropDownContext.Provider>
  );
}

Dropdown.TwoColumnLayout = DropdownTwoColumn;
Dropdown.SingleColumnLayout = DropdownColumn;
